"use client"

import React, { useEffect, useMemo, useState } from 'react'
import DashboardContentWrapper from '@/shared/components/dashboard-content-wrapper'
import Heading from '@/shared/components/ui/typography/heading'
import PartnerForm from '@/features/partners/components/partner-form'
import PartnerFormProvider from '@/features/partners/components/forms/providers/partner-form-provider'
import { ChevronLeft } from 'lucide-react'
import { useRouter, useParams } from 'next/navigation'
import { Partner } from '@/features/partners/types/partner.type'
import { useRegionId } from '@/shared/hooks/use-region-id'
import { buildRegionUrl } from '@/shared/lib/utils'
import { useRegion } from '@/shared/hooks/use-region'
import { SEARCH_PARAMS_TAB_KEY } from '@/features/admin/constants/admin-tabs'
import { useSession } from '@/features/auth/context/session.context'
import { UserRole } from '@/shared/constants/user-roles'
import ScrollToTopFab from '@/shared/components/scroll-to-top-fab'

interface EditPartnerPageProps {}

export default function EditPartnerPage() {
  const router = useRouter()
  const resolvedParams = useParams() as any
  const id = resolvedParams?.id
  const { regionId, regionSlug, region } = useRegionId()
  const { regionSlug: regionSlugFromHook } = useRegion()
  const { session, isLoading: sessionLoading } = useSession()

  const [partnerData, setPartnerData] = useState<Partial<Partner> | undefined>(undefined)
  const [regionalDefaults, setRegionalDefaults] = useState<Record<string, unknown>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // Only ADMIN and BASE_LEADER can edit partners
  const canManagePartners = session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER
  
  // Redirect if user doesn't have permission
  useEffect(() => {
    if (!sessionLoading && !canManagePartners) {
      const href = buildRegionUrl(regionSlugFromHook || regionSlug, '/partners')
      router.push(href)
    }
  }, [sessionLoading, canManagePartners, router, regionSlugFromHook, regionSlug])

  // Fetch partner data - only fetch if user has permission
  useEffect(() => {
    const fetchData = async () => {
      if (!id || !regionId) return

      setLoading(true)
      setError(null)

      try {
        const [organizationResponse, associationResponse] = await Promise.all([
          fetch(`/api/organizations/${id}`),
          fetch(`/api/organization-regions?regionId=${regionId}&organizationId=${id}`),
        ])

        if (!organizationResponse.ok) {
          const errorData = await organizationResponse.json().catch(() => ({}))
          throw new Error(errorData?.error ?? 'Failed to load organization')
        }

        const organizationPayload = await organizationResponse.json()
        const organization = organizationPayload?.success ? organizationPayload.data : organizationPayload
        setPartnerData(organization)

        if (associationResponse.ok) {
          const associationPayload = await associationResponse.json()
          const association = associationPayload?.success ? associationPayload.data : associationPayload

          if (association) {
            setRegionalDefaults({
              regionalContactPerson: association.contactPerson ?? '',
              regionalContactPhone: association.contactPhone ?? '',
              regionalContactEmail: association.contactEmail ?? '',
              regionalIsActive: association.isActive ?? true,
              regionAssociationId: association.id,
            })
          } else {
            setRegionalDefaults({
              regionalIsActive: true,
            })
          }
        } else {
          setRegionalDefaults({
            regionalIsActive: true,
          })
        }
      } catch (err) {
        console.error('Error fetching partner data:', err)
        setError(err instanceof Error ? err.message : 'Failed to load partner')
      } finally {
        setLoading(false)
      }
    }

    // Only fetch if user has permission
    if (id && regionId && canManagePartners && !sessionLoading) {
      fetchData()
    }
  }, [id, regionId, canManagePartners, sessionLoading])

  const defaultValues = useMemo(() => {
    if (!partnerData) return undefined
    return {
      ...partnerData,
      ...regionalDefaults,
    }
  }, [partnerData, regionalDefaults])

  const handleBack = () => {
    const href = buildRegionUrl(regionSlugFromHook || regionSlug, '/admin', { [SEARCH_PARAMS_TAB_KEY]: 'partners' })
    router.push(href)
  }

  const handleSubmit = async (data: any) => {
    if (!regionId) {
      setError('Region not available. Please select a region and try again.')
      return
    }

    try {
      setError(null)

      const {
        regionalContactPerson,
        regionalContactPhone,
        regionalContactEmail,
        regionalIsActive,
        regionAssociationId,
        ...organizationPayload
      } = data

      const updateResponse = await fetch(`/api/organizations/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(organizationPayload),
      })

      if (!updateResponse.ok) {
        const errorData = await updateResponse.json().catch(() => ({}))
        throw new Error(errorData?.error ?? 'Failed to update organization')
      }

      if (regionAssociationId) {
        const associationUpdate = await fetch(`/api/organization-regions/${regionAssociationId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            isActive: regionalIsActive,
            contactPerson: regionalContactPerson,
            contactPhone: regionalContactPhone,
            contactEmail: regionalContactEmail,
          }),
        })

        if (!associationUpdate.ok) {
          const errorData = await associationUpdate.json().catch(() => ({}))
          throw new Error(errorData?.error ?? 'Failed to update regional settings')
        }
      } else {
        const associationCreate = await fetch('/api/organization-regions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            organizationId: Number(id),
            regionId,
            isActive: regionalIsActive,
            contactPerson: regionalContactPerson,
            contactPhone: regionalContactPhone,
            contactEmail: regionalContactEmail,
          }),
        })

        if (!associationCreate.ok) {
          const errorData = await associationCreate.json().catch(() => ({}))
          throw new Error(errorData?.error ?? 'Failed to create regional settings')
        }
      }

      const href = buildRegionUrl(regionSlugFromHook || regionSlug, '/admin', { [SEARCH_PARAMS_TAB_KEY]: 'partners' })
      router.push(href)
    } catch (err) {
      console.error('Error updating partner:', err)
      setError(err instanceof Error ? err.message : 'Unexpected error updating partner')
    }
  }

  // Show loading state while checking permissions or fetching data
  if (sessionLoading || !canManagePartners) {
    return (
      <DashboardContentWrapper className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </DashboardContentWrapper>
    )
  }

  if (loading || !partnerData) {
    return (
      <DashboardContentWrapper className="flex flex-col gap-7">
        <div className="flex items-center gap-3 md:pb-2">
          <button onClick={handleBack}>
            <ChevronLeft className="w-7 h-7" />
          </button>
          <div>
            <Heading>Edit Partner</Heading>
            <p className="text-muted-foreground text-sm mt-1">
              Loading partner details...
            </p>
          </div>
        </div>
      </DashboardContentWrapper>
    )
  }

  return (
    <DashboardContentWrapper className="flex flex-col gap-7 pb-24 md:pb-0">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-3 md:pb-2">
        <div className="flex items-center gap-3">
          <button onClick={handleBack}>
            <ChevronLeft className="w-7 h-7" />
          </button>
          <div>
            <Heading>{region?.name ? `Edit ${partnerData.name} (${region.name})` : 'Edit Partner'}</Heading>
            <p className="text-muted-foreground text-sm mt-1">
              Modify partner organization details and regional settings
            </p>
          </div>
        </div>
        {error && (
          <p className="text-sm text-destructive">{error}</p>
        )}
      </div>

      <PartnerFormProvider onSubmit={handleSubmit} defaultValues={defaultValues}>
        <PartnerForm partnerId={id} />
      </PartnerFormProvider>
      <ScrollToTopFab />
    </DashboardContentWrapper>
  )
}
